// The cold caller's screen: every contact in one clear list (search, city,
// status), a detail window per contact with the call history and the result
// buttons. Everyone calls from the same pool (docs/specs/cold-calling.md).

import { useEffect, useRef, useState } from 'react';
import {
  CALL_OUTCOMES,
  claimCall,
  logCall,
  releaseCall,
  useCallHistory,
  useColdStats,
  usePool,
  type CallOutcomeId,
  type PoolFilter,
  type PoolRow,
} from '../../lib/data';
import { supabase } from '../../lib/supabase';
import { fDay, fShort } from '../../lib/format';
import { ErrorBox, useToast } from '../../ui/kit';
import { Icon } from '../../ui/icons';

const OUTCOME_LABEL = Object.fromEntries(CALL_OUTCOMES.map((o) => [o.id, o.label]));
const FILTERS: { id: PoolFilter; label: string }[] = [
  { id: 'todo', label: 'To call' },
  { id: 'followup', label: 'Follow-ups' },
  { id: 'called', label: 'Called' },
  { id: 'done', label: 'Finished' },
  { id: 'all', label: 'All' },
];
const PAGE = 40;

/** The state of a contact in one short pill. */
export function statusPill(
  r: Pick<PoolRow, 'attempts' | 'status' | 'tag' | 'history' | 'next_attempt_at'>,
) {
  const last = r.history[0]?.outcome;
  if (r.status === 'done') {
    if (last === 'booked') return ['Booked', 'te'] as const;
    if (last === 'not_interested') return ['Not interested', 'red'] as const;
    if (last === 'send_email') return ['Email requested', ''] as const;
    if (last === 'wrong_number') return ['Wrong number', 'gr'] as const;
    return ['Finished', 'gr'] as const;
  }
  if (r.attempts === 0) return ['New', 'te'] as const;
  if (last === 'call_back' && r.tag) return [r.tag, 'or'] as const;
  return [`${OUTCOME_LABEL[last ?? ''] ?? 'Called'}, try ${r.attempts + 1} of 3`, 'or'] as const;
}

function tomorrowAt(h: number): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(h, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:00`;
}

export function Queue({ onBack }: { onBack?: (() => void) | undefined }) {
  const stats = useColdStats();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [typed, setTyped] = useState('');
  const [city, setCity] = useState('');
  const [filter, setFilter] = useState<PoolFilter>('todo');
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState<PoolRow | null>(null);
  const [busy, setBusy] = useState(false);
  const pool = usePool({ search, city, filter, limit });

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(typed.trim());
      setLimit(PAGE);
    }, 250);
    return () => clearTimeout(t);
  }, [typed]);

  const d = pool.data;
  const cities = (d?.cities ?? []).slice(0, 6);

  // "Call next": the contact first in line, held for this caller.
  async function callNext() {
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc('next_call');
      if (error) throw new Error(error.message);
      if (!data) return toast('Nothing to call right now');
      const row = (await supabase.rpc('call_pool', {
        p_search: (data as { phone_e164: string }).phone_e164,
        p_filter: 'all',
        p_limit: 1,
        p_offset: 0,
      })) as { data: { rows: PoolRow[] } | null };
      if (row.data?.rows[0]) setOpen(row.data.rows[0]);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const t = stats.data?.today;

  return (
    <div className="ccx">
      <div className="head">
        <div>
          <h1>
            My call <span className="grad">queue</span>
          </h1>
          <p>
            {stats.data ? `${stats.data.queue.ready} contacts to call now` : 'Contacts to call'}
            {stats.data && stats.data.queue.scheduled
              ? `, ${stats.data.queue.scheduled} follow-ups scheduled`
              : ''}
            . Everyone calls from the same list, so log every call.
          </p>
        </div>
        <div className="head-r">
          {onBack && (
            <button className="btn big" onClick={onBack}>
              Back to numbers
            </button>
          )}
          <button className="btn primary big" disabled={busy} onClick={() => void callNext()}>
            Call next <Icon name="arrow" />
          </button>
        </div>
      </div>
      <ErrorBox error={pool.error ?? stats.error} />
      {stats.data && t && (
        <div className="cc-strip" aria-label="Calls today">
          <span>
            Dials today <b>{t.dials}</b> / {stats.data.target}
          </span>
          <span>
            Reached <b>{t.reached}</b>
          </span>
          <span>
            Conversations <b>{t.conversations}</b>
          </span>
          <span>
            Booked <b>{t.booked}</b>
          </span>
        </div>
      )}

      <section className="card sec" data-testid="cc-list">
        <div className="cc-bar">
          <label className="cc-search">
            <Icon name="search" />
            <input
              type="search"
              aria-label="Search clinic or phone"
              placeholder="Search clinic or phone…"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
          <div className="cc-chips" role="group" aria-label="City">
            <button
              className="cc-chip"
              aria-pressed={city === ''}
              onClick={() => {
                setCity('');
                setLimit(PAGE);
              }}
            >
              All cities
            </button>
            {cities.map((c) => (
              <button
                key={c.city}
                className="cc-chip"
                aria-pressed={city === c.city}
                onClick={() => {
                  setCity(c.city);
                  setLimit(PAGE);
                }}
              >
                {c.city}
              </button>
            ))}
          </div>
          <label className="cc-select">
            <span className="sr-only">Status</span>
            <select
              aria-label="Status"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as PoolFilter);
                setLimit(PAGE);
              }}
            >
              {FILTERS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="cc-table" role="table" aria-label="Contacts">
          <div className="cc-row cc-head" role="row">
            <span role="columnheader">Clinic</span>
            <span role="columnheader">Phone</span>
            <span role="columnheader">City</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Notes</span>
            <span role="columnheader">Last call</span>
          </div>
          {!d && <div className="loading">Loading…</div>}
          {d && d.rows.length === 0 && (
            <div className="empty" style={{ padding: 28 }}>
              No contacts match. Try another status or city.
            </div>
          )}
          {d?.rows.map((r) => {
            const [label, cls] = statusPill(r);
            return (
              <button
                key={r.id}
                className="cc-row"
                role="row"
                onClick={() => setOpen(r)}
                aria-label={`Open ${r.clinic}`}
              >
                <span className="cc-main" role="cell">
                  <b>{r.clinic}</b>
                  <small>{r.locality}</small>
                  {(r.fb_ads || r.google_ads) && <em>Runs ads</em>}
                </span>
                <span role="cell" className="cc-phone">
                  {r.phone_e164}
                </span>
                <span role="cell">{r.city && <span className="pill sm te">{r.city}</span>}</span>
                <span role="cell">
                  <span className={`pill sm ${cls}`}>{label}</span>
                  {r.held_by && <small className="cc-held">In use by {r.held_by}</small>}
                </span>
                <span role="cell" className="cc-notes">
                  {r.history.length === 0 ? (
                    <span className="muted">No notes yet</span>
                  ) : (
                    r.history.map((h, i) => (
                      <span key={i}>
                        <small>
                          {fDay(h.at)} · {h.caller}
                        </small>
                        {OUTCOME_LABEL[h.outcome] ?? h.outcome}
                        {h.note ? `: ${h.note}` : ''}
                      </span>
                    ))
                  )}
                </span>
                <span role="cell" className="cc-date">
                  {r.last_call_at ? fShort(r.last_call_at) : '–'}
                </span>
              </button>
            );
          })}
        </div>
        {d && d.rows.length < d.total && (
          <div style={{ padding: 16, textAlign: 'center' }}>
            <button className="btn" onClick={() => setLimit(limit + PAGE)}>
              Show more ({d.rows.length} of {d.total})
            </button>
          </div>
        )}
        {d && d.rows.length > 0 && d.rows.length >= d.total && (
          <div className="m" style={{ padding: 14, textAlign: 'center' }}>
            {d.total} contact{d.total === 1 ? '' : 's'}
          </div>
        )}
      </section>

      {open && (
        <ContactModal
          key={open.id}
          row={open}
          onClose={() => {
            setOpen(null);
            void pool.refetch();
            void stats.refetch();
          }}
        />
      )}
    </div>
  );
}

// One contact, in a window over the list ------------------------------------------

function ContactModal({ row, onClose }: { row: PoolRow; onClose: () => void }) {
  const toast = useToast();
  const history = useCallHistory(row.id);
  const [note, setNote] = useState('');
  const [callback, setCallback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [held, setHeld] = useState<string | null>(null);
  const [logged, setLogged] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [label, cls] = statusPill(row);
  const finished = row.status === 'done';

  // Hold the contact while the window is open; hand it back when it closes.
  useEffect(() => {
    let mine = false;
    if (!finished) {
      void claimCall(row.id).then((r) => {
        if (r.ok) mine = true;
        else if (r.reason === 'held') setHeld(r.by ?? 'someone');
      });
    }
    return () => {
      if (mine) void releaseCall(row.id, false);
    };
  }, [row.id, finished]);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function log(outcome: CallOutcomeId) {
    if (busy || held) return;
    if (outcome === 'call_back' && callback === null) {
      setCallback(tomorrowAt(10));
      return;
    }
    if (outcome !== 'no_answer' && outcome !== 'wrong_number' && !note.trim()) {
      toast('Write a short note about the call first');
      return;
    }
    setBusy(true);
    try {
      await logCall(
        row.id,
        outcome,
        note,
        outcome === 'call_back' && callback ? new Date(callback).toISOString() : null,
      );
      setLogged(true);
      toast(outcome === 'booked' ? 'Booked. Lead created in the pipeline.' : 'Call logged');
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="cc-scrim" onClick={onClose} />
      <div
        className="cc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cc-title"
        ref={ref}
        data-testid="cc-current"
        data-logged={logged}
      >
        <div className="cc-mhead">
          <h2 id="cc-title">{row.clinic}</h2>
          <button className="x" aria-label="Close" onClick={onClose} type="button">
            ×
          </button>
        </div>
        <div className="cc-mbody">
          <div className="cc-pills">
            <span className="mk">{row.market}</span>
            {row.city && <span className="pill sm te">{row.city}</span>}
            <span className={`pill sm ${cls}`}>{label}</span>
            {row.fb_ads && <span className="pill sm te">Runs Facebook ads</span>}
            {row.google_ads && <span className="pill sm te">Runs Google ads</span>}
          </div>
          {row.locality && <div className="cc-loc">{row.locality}</div>}
          <a className="btn cc-call" href={`tel:${row.phone_e164}`}>
            <Icon name="phone" /> {row.phone_e164}
          </a>
          <div className="cc-links">
            {row.website && (
              <a href={row.website} target="_blank" rel="noreferrer noopener">
                {row.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
              </a>
            )}
            {row.email && <a href={`mailto:${row.email}`}>{row.email}</a>}
          </div>

          <div className="cc-box" data-testid="cc-history">
            <b>Notes and calls</b>
            {history.isLoading && <div className="m">Loading…</div>}
            {history.data?.length === 0 && <div className="m">Nobody has called yet.</div>}
            {history.data?.map((h, i) => (
              <div key={i} className="cc-hist">
                <small>
                  {fShort(h.at)} · {h.caller}
                </small>
                <span>
                  <b>{OUTCOME_LABEL[h.outcome] ?? h.outcome}</b>
                  {h.note ? `: ${h.note}` : ''}
                </span>
              </div>
            ))}
          </div>

          {finished ? (
            <p className="m">This contact is finished, so nothing more is logged.</p>
          ) : held ? (
            <p className="errbox" role="status">
              {held} has this contact open right now. Try another one.
            </p>
          ) : (
            <>
              <label className="field">
                <span>Note (needed for every result except no answer and wrong number)</span>
                <textarea
                  rows={2}
                  aria-label="Note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Owner name, best time, what they said"
                />
              </label>
              {callback !== null && (
                <label className="field">
                  <span>Call back on</span>
                  <input
                    type="datetime-local"
                    aria-label="Call back on"
                    value={callback}
                    onChange={(e) => setCallback(e.target.value)}
                  />
                </label>
              )}
              <div className="qbtns">
                {CALL_OUTCOMES.map((o) => (
                  <button
                    key={o.id}
                    className={`btn o-${o.id}`}
                    disabled={busy}
                    onClick={() => void log(o.id)}
                  >
                    {o.id === 'call_back' && callback !== null ? 'Confirm call back' : o.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
